import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Layers } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { MESSAGE_TYPES, Sel, Fld } from "./shared";

export default function Templates({ refs, canWrite, onEdit, reload }) {
  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/message-templates/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      {canWrite && <Button onClick={() => onEdit({})} data-testid="new-template-btn" className="rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New Template</Button>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {refs.templates.map((t) => (
          <div key={t.id} data-testid={`template-${t.id}`} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start justify-between">
              <div><p className="font-heading font-semibold flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> {t.name}</p><Badge className="rounded-sm font-normal bg-muted text-muted-foreground mt-1">{t.category}</Badge></div>
              {canWrite && <div className="flex gap-2"><button onClick={() => onEdit(t)} data-testid={`edit-template-${t.id}`} className="text-xs text-primary hover:underline">Edit</button>{!t.is_default && <button onClick={() => del(t.id)} data-testid={`del-template-${t.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}</div>}
            </div>
            {t.subject && <p className="text-xs font-medium mt-2">{t.subject}</p>}
            <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{t.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TemplateDialog({ tpl, onClose, onSaved }) {
  const isNew = !tpl.id;
  const [f, setF] = useState({ name: tpl.name || "", category: tpl.category || "General Announcement", subject: tpl.subject || "", body: tpl.body || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || !f.body.trim()) { toast.error("Name and body required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/message-templates", f); else await api.put(`/message-templates/${tpl.id}`, f);
      toast.success("Template saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New Template" : "Edit Template"}</DialogTitle><DialogDescription>Reusable message with {"{{placeholders}}"}.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <Fld label="Name"><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tpl-name" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Category"><Sel testid="tpl-category" value={f.category} onChange={(v) => set("category", v)} options={MESSAGE_TYPES.map((t) => [t, t])} /></Fld>
          <Fld label="Subject"><input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="tpl-subject" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Body"><textarea value={f.body} onChange={(e) => set("body", e.target.value)} rows={6} data-testid="tpl-body" className="w-full rounded-lg border border-border bg-background p-2 text-sm resize-none" /></Fld>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button><Button className="rounded-xl" onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
